
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EXPIRED_PRACTICES_QUERY_KEY } from "../../config";
import { getExpiredActions, getExpiredActionsForView } from "../../services/http.services";
import { AxiosError } from "axios";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { useRecoilState, useRecoilValue } from "recoil";
import { avaiableExpiredActionsState, practiceDetail } from "../../state";
import CustomLoader from "../../../../util/component/CustomLoader";

interface ActionMenuButtonProps {
    forView?: boolean,
    disabled?: boolean,
    handleInsertAction: (actionType: string) => void
}
const ActionMenuButton = ({
    forView = false, 
    disabled = false,
    handleInsertAction
}: ActionMenuButtonProps) => {

    const theme = useTheme();
    const {t} = useTranslation(['expiredManagement', 'translation']);
    const {dispatchGlobalData} = useContext(GlobalContext);

    const [openInsertActionMenu, setOpenInsertActionMenu] = useState(false);
    const [avaiableActions, setAvaiableActions] = useRecoilState(avaiableExpiredActionsState);

    const expiredPracticeDetail = useRecoilValue(practiceDetail);

    const insertActionRef = useRef<HTMLButtonElement>(null);

    const handleToggleInsertAction = () => {
        setOpenInsertActionMenu(!openInsertActionMenu);
    }

    const handleCloseInsertAction = () => {
        setOpenInsertActionMenu(false);
    }

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenInsertActionMenu(false);
        } else if (event.key === 'Escape') {
            setOpenInsertActionMenu(false);
        }
    }

    const getExpiredActionsQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.GET_AVAIABLE_EXPIRED_ACTIONS],
        {
            enabled: false,
            queryFn: () => {
                return getExpiredActions(expiredPracticeDetail.status);
            },
            onSuccess: (response) => {
                setAvaiableActions(response?.data || []);
            },
            onError: (error: AxiosError) => {
                setAvaiableActions([]);
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const getExpiredActionsForViewQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.GET_AVAIABLE_EXPIRED_ACTIONS_FOR_VIEW],
        {
            enabled: false,
            queryFn: () => {
                return getExpiredActionsForView();
            },
            onSuccess: (response) => {
                setAvaiableActions(response?.data || []);
            },
            onError: (error: AxiosError) => {
                setAvaiableActions([]);
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    //Trigger per Search Practices
    useEffect(() => {
        if(forView)
            getExpiredActionsForViewQuery.refetch();

    }, [forView]);

    return (
        <>
        <Button 
            sx={{ml: forView ? '0' : theme.spacing(1)}}
                ref={insertActionRef}
                id="insert-action-button"
                disabled={forView && disabled}
                onClick={handleToggleInsertAction}
            >
                {t('common.button.action')}
            </Button>

            <Popper
                open={openInsertActionMenu}
                anchorEl={insertActionRef.current}
                role={undefined}

                placement="bottom-start"
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleCloseInsertAction}>
                                <MenuList
                                    autoFocusItem={openInsertActionMenu}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {avaiableActions.map(action => 
                                        <MenuItem
                                            key={action}
                                            onClick={() => handleInsertAction(action)}
                                        >
                                            {t(`common.actions.${action}`)}
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>

            <CustomLoader loading={getExpiredActionsQuery.isFetching || getExpiredActionsForViewQuery.isFetching} />
        </>
    )
}

export default ActionMenuButton;